import type {ChangeEvent, DragEvent} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useFormField, type ValidationFunc} from '@nfq/react-form';
import {useLocalObservable} from 'mobx-react-lite';

export interface ProgressState {
    fileErrors: (string | null | undefined)[];
    fileProgressBars: (number | 'done' | undefined)[];
    setError(index: number, progressError: string | null | undefined): void;
    setProgress(index: number, progress: number | 'done' | undefined): void;
}

/**
 * Custom hook for handling file input functionality with upload progress tracking.
 * Provides file selection, validation, upload progress monitoring, and error handling.
 *
 * @param name                               The name of the form field.
 * @param defaultProgresses                  Default progress states for file uploads, including errors and progress bars.
 * @param defaultProgresses.fileErrors       An array of error messages corresponding to each file upload, where the index matches the file index.
 * @param defaultProgresses.fileProgressBars An array of progress values (0-100 or 'done') corresponding to each file upload, where the index matches the file index.
 * @param defaultValue                       The default value for the form field.
 * @param onChange                           Callback function triggered when the value changes.
 * @param maxFileSize                        Maximum allowed file size in bytes.
 * @param keepOnUnmount                      Whether to keep the field value when component unmounts.
 * @param validation                         Validation function for the form field.
 * @returns Object containing file input handlers, state, and form field properties.
 */
export const useFileInput = (
    name: string,
    defaultProgresses: {
        fileErrors: (string | null | undefined)[];
        fileProgressBars: (number | 'done' | undefined)[];
    },
    defaultValue: FileList,
    onChange: (value: FileList, progresses: ProgressState) => Promise<void>,
    maxFileSize: number,
    keepOnUnmount = false,
    validation: ValidationFunc = () => null
) => {
    const {error, fieldRef, handleFieldChange, value} = useFormField<FileList, HTMLInputElement>(
        name,
        defaultValue,
        defaultValue,
        validation,
        keepOnUnmount
    );

    const {handleFieldChange: handleProgressesFieldChange, value: progressesFieldValue}
        = useFormField(
            `${name}-progresses`,
            defaultProgresses.fileProgressBars.map(
                progressToMap => (progressToMap === undefined ? '' : progressToMap.toString())
            ),
            [],
            () => null,
            keepOnUnmount
        );

    const {handleFieldChange: handleErrorsFieldChange, value: errorsFieldValue}
        = useFormField(
            `${name}-errors`,
            defaultProgresses.fileErrors.map(errorToMap => errorToMap ?? ''),
            [],
            () => null,
            keepOnUnmount
        );

    const colors = useThemeColors();

    const progresses = useLocalObservable<ProgressState>(() => ({
        fileErrors: errorsFieldValue.map(errorToMap => (errorToMap === '' ? undefined : errorToMap)),
        fileProgressBars: progressesFieldValue.map(progress => {
            if (progress === '') return undefined;
            if (progress === 'done') return 'done';

            return Number(progress);
        }),
        /**
         * Sets an error message for a specific file upload.
         *
         * @param index         The index of the file in the upload list.
         * @param progressError The error message to associate with the file.
         */
        setError(index: number, progressError: string) {
            this.fileErrors[index] = progressError;
            handleErrorsFieldChange(this.fileErrors.map(errorToMap => errorToMap ?? ''));
        },
        /**
         * Updates the upload progress of a specific file.
         *
         * @param index    The index of the file in the upload list.
         * @param progress The upload progress percentage (0-100) or `'done'` when complete.
         */
        setProgress(index: number, progress: number) {
            this.fileProgressBars[index] = progress;
            handleProgressesFieldChange(
                this.fileProgressBars.map(
                    progressToMap => (progressToMap === undefined ? '' : progressToMap.toString())
                )
            );
        }
    }));

    /**
     * Handles file selection and initiates the upload process.
     * This function reads the selected files, checks for file size constraints,
     * and uploads each file while tracking progress.
     *
     * @param event The change event from the file input element.
     */
    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const newFileList = event.target.files ?? new DataTransfer().files;

        for (let i = 0; i < newFileList.length; i++) {
            if (newFileList[i].size > maxFileSize) {
                progresses.setError(i, 'Die Datei ist zu groß.');

                return;
            }
        }

        await onChange(newFileList, progresses);
        handleFieldChange(newFileList);
    };

    /**
     * Deletes a file from the upload list.
     * This function resets the progress and error states for the deleted file
     * and removes it from the `fileList` array.
     *
     * @param fileIndex The index of the file to be removed.
     */
    const handleFileDelete = (fileIndex: number) => {
        const newProgresses = Array.from(progresses.fileProgressBars).filter((_, index) => index !== fileIndex);
        const newErrors = Array.from(progresses.fileErrors).filter((_, index) => index !== fileIndex);

        for (let i = 0; i < newProgresses.length; i++) {
            if (i >= fileIndex) {
                progresses.setProgress(i, newProgresses[i]);
            }
        }

        for (let i = 0; i < newErrors.length; i++) {
            if (i >= fileIndex) {
                progresses.setError(i, newErrors[i]);
            }
        }

        const dataTransfer = new DataTransfer();

        Array.from(value)
            .filter((_, index) => index !== fileIndex)
            .forEach(file => dataTransfer.items.add(file));

        if (!fieldRef.current) return;

        fieldRef.current.files = dataTransfer.files;
        fieldRef.current.dispatchEvent(new Event('change', {bubbles: true}));
    };

    /**
     * Handles the drag-over event for file inputs.
     * Prevents default behavior and sets the appropriate drop effect.
     *
     * @param event The drag event from the file input.
     */
    const handleDragOver = (event: DragEvent<HTMLInputElement>): void => {
        event.stopPropagation();
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.dropEffect = 'copy';
    };

    return {
        colors,
        error,
        fieldRef,
        handleChange,
        handleDragOver,
        handleFileDelete,
        progresses,
        value
    };
};